import { useAuth0 } from '@auth0/auth0-react';
import { AccountProvider } from '@my/platform/account';
import { DashboardLayout } from '@my/ui';
import { AccountContextBar } from './AccountContextBar';
import { AppSidebar } from './AppSidebar';
import { useUiStore } from '@/stores/ui';

const ACCOUNT_ID_CLAIM = 'https://my/account_id';

function getAccountIdClaim(user: ReturnType<typeof useAuth0>['user']): string {
  if (!user) return '';
  const raw = (user as Record<string, unknown>)[ACCOUNT_ID_CLAIM];
  return typeof raw === 'string' ? raw : '';
}

/**
 * My Hub's shell.
 *
 * <p>
 * `AccountProvider` is REQUIRED, not optional: the sidebar's account switcher
 * reads `useAccount()`, which throws outright when the provider is missing —
 * one blank screen rather than a degraded one. A Stratum identity carries no
 * `account_id` claim, so it simply starts empty for them, and the support strip
 * supplies the account instead.
 * </p>
 */
export function AppShell() {
  const { user } = useAuth0();
  const isOpen = useUiStore((s) => s.isSidebarOpen);
  const toggle = useUiStore((s) => s.toggleSidebar);

  return (
    <AccountProvider defaultAccountId={getAccountIdClaim(user)}>
      <DashboardLayout
        sidebar={<AppSidebar />}
        isOpen={isOpen}
        onToggle={toggle}
        // Which customer is selected, across the bottom of the shell. The piece
        // that disappears the day customers manage their own account here.
        banner={<AccountContextBar />}
      />
    </AccountProvider>
  );
}
